import { DashboardLayout } from "components/Dashboard/DashboardLayout";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { Suspense } from "react";

import { ManifestDetailDrawer } from "../../Manifests/ManifestDetailDrawer";
import { FacilityDetailsTile } from "./FacilityDetailsTile";
import { GeneratorStatusTile } from "./GeneratorStatusTile";
import { ManifestLineItemsTile } from "./ManifestLineItemsTile";
import { WasteAccumulationAreasTile } from "./WasteAccumulationAreasTile";
import { WasteContainersTile } from "./WasteContainersTile";
import { WasteEpisodicEventsTile } from "./WasteEpisodicEventsTile";
import { useWasteFacilityState, WasteFacilityProvider } from "./WasteFacilityContext";
import { WasteFacilityFilters } from "./WasteFacilityFilters";
import { WasteFacilitySkeleton } from "./WasteFacilitySkeleton";
import { WasteGeneratedTile } from "./WasteGenerated/WasteGeneratedTile";
import { WasteShipmentsTile } from "./WasteShipments/WasteShipmentTile";
import { WasteStreamsTile } from "./WasteStreamsTile";
import { WasteStreamsTile as WasteStreamsTileV1 } from "./WasteStreamsTileV1";

/**
 * Container Tracking, Accumulation Areas, and Episodic events all lay out
 * from one decision: whether the facility's state has adopted subpart L
 * (`episodicEventsApplicable`). Episodic events never apply in a
 * non-adopting state, so that tile is dropped there entirely, and the other
 * two reflow to fill the freed width rather than leaving a gap:
 *
 * - Adopting state: Container Tracking takes the full row on its own line;
 *   Accumulation Areas and Episodic events split the row below it 50/50.
 * - Non-adopting state: Container Tracking and Accumulation Areas split one
 *   row 50/50, and Episodic events is absent.
 *
 * Pulled into its own component (rather than inlined in `WasteFacility`)
 * because it needs `useWasteFacilityState`, which only resolves inside
 * `WasteFacilityProvider`.
 */
export function ContainerAccumulationAndEpisodicTiles() {
  const { episodicEventsApplicable } = useWasteFacilityState();
  return (
    <>
      <WasteContainersTile fullWidth={episodicEventsApplicable} />
      <WasteAccumulationAreasTile />
      {episodicEventsApplicable && <WasteEpisodicEventsTile />}
    </>
  );
}

export const WasteFacility = () => {
  const { tenantId, isAmazon } = useTenant();
  const { featureFlags } = useFeatureFlags();
  // Customers see the v2 Waste Stream grid/drawer only when manifest-views-v2
  // is on; otherwise the v1 view. Staff always v2.
  const showManifestViewsV2 = useManifestViewsV2();
  // Two-layer gate: the card sources from Amazon's WADM feed exclusively, so
  // `isAmazon` is the hard constraint — no other tenant should ever see it
  // even if the flag is mistakenly enabled. The flag then controls rollout
  // WITHIN Amazon (so we can stage the card, run QA, A/B per-facility, etc.).
  const wasteGenerationCardEnabled = isAmazon && featureFlags[FeatureFlagKey.WasteGenerationCard];
  const containerTrackingEnabled = featureFlags[FeatureFlagKey.ContainerTracking];
  const { selectedId, popIdFromUrl } = useSelectedIdInUrl("wasteLineUnifiedId", {
    prefixSegment: "manifest-line-items",
  });
  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Facilities",
      to: tenantId ? `/o/${tenantId}/waste/facilities` : undefined,
    },
  ]);

  return (
    <Suspense fallback={<WasteFacilitySkeleton />}>
      <WasteFacilityProvider>
        <WasteFacilityFilters />
        <DashboardLayout>
          <FacilityDetailsTile />
          <GeneratorStatusTile />
          <WasteShipmentsTile />
          {/*
            Waste Generated is Amazon-only AND flag-gated. Both must be true.
            Staff override on the flag lets us QA inside the Amazon tenant
            (and only the Amazon tenant) without flipping tenant configs.
          */}
          {wasteGenerationCardEnabled && <WasteGeneratedTile />}
          {containerTrackingEnabled && <ContainerAccumulationAndEpisodicTiles />}
          {showManifestViewsV2 ? <WasteStreamsTile /> : <WasteStreamsTileV1 />}
          <ManifestLineItemsTile />
        </DashboardLayout>
        <ManifestDetailDrawer lineItemId={selectedId} open={!!selectedId} onClose={popIdFromUrl} />
      </WasteFacilityProvider>
    </Suspense>
  );
};
